import React from 'react';
import { Inbox } from 'lucide-react';
import { Article, Event, Album, Book, Canticle } from '../../types';
import { EntityCard } from './EntityCard';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface EntityGridProps {
  items: (Article | Event | Album | Book | Canticle)[];
  type: SelectedType;
  loading: boolean;
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    total: number;
  };
  onPageChange: (page: number) => void;
  onEdit: (item: Article | Event | Album | Book | Canticle) => void;
  onDelete: (item: Article | Event | Album | Book | Canticle) => void;
}

export const EntityGrid: React.FC<EntityGridProps> = ({
  items,
  type,
  loading,
  pagination,
  onPageChange,
  onEdit,
  onDelete,
}) => {
  const totalPages = Math.ceil(pagination.total / pagination.itemsPerPage);

  if (loading) {
    return (
      <div className='flex justify-center items-center py-16'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
          <p className='text-gray-600'>Chargement...</p>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className='text-center py-16'>
        <Inbox className='w-16 h-16 mx-auto mb-4 text-gray-300' />
        <h2 className='text-xl font-semibold text-gray-900 mb-2'>
          Aucun élément trouvé
        </h2>
        <p className='text-gray-600'>
          Aucun élément ne correspond aux critères de recherche.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6'>
        {items.map((item) => (
          <EntityCard
            key={item.id}
            item={item}
            type={type}
            onEdit={() => onEdit(item)}
            onDelete={() => onDelete(item)}
          />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-center gap-2 mt-6'>
          <button
            onClick={() => onPageChange(pagination.currentPage - 1)}
            disabled={pagination.currentPage === 1}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            Précédent
          </button>

          <div className='flex gap-1'>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((page) => {
                // Show first page, last page, current page, and pages around current
                if (page === 1 || page === totalPages) return true;
                if (Math.abs(page - pagination.currentPage) <= 1) return true;
                return false;
              })
              .map((page, index, array) => {
                // Add ellipsis if there's a gap
                const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                return (
                  <React.Fragment key={page}>
                    {showEllipsisBefore && (
                      <span className='px-2 py-2 text-gray-500'>...</span>
                    )}
                    <button
                      onClick={() => onPageChange(page)}
                      className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                        pagination.currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                );
              })}
          </div>

          <button
            onClick={() => onPageChange(pagination.currentPage + 1)}
            disabled={pagination.currentPage === totalPages}
            className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
          >
            Suivant
          </button>
        </div>
      )}

      {/* Pagination Info */}
      <div className='text-center text-sm text-gray-600 mt-4'>
        Page {pagination.currentPage} sur {totalPages} • {pagination.total} élément(s)
        au total
      </div>
    </div>
  );
};
